import React from 'react';
import {
    Box,
    Stack,
    Button,
    IconButton,
    InputBase,
    Select,
    MenuItem,
    Typography,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { styled } from '@mui/material/styles';
import ExportReportModal from './ExportReportModal';

const tabs = ['Not Reviewed', 'Review Passed', 'Fully Approved', 'Review Failed', 'Rejected'];

const PillButton = styled(Button, {
    shouldForwardProp: prop => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
    minWidth: '48px',
    borderRadius: active ? '24px' : '6px',
    padding: '6px 24px',
    minHeight: '40px',
    fontSize: '14px',
    fontWeight: 500,
    textTransform: 'none',
    backgroundColor: active ? '#132F5F' : '#F2F0F0',
    color: active ? '#FFFFFF' : '#000000',
    border: 'none',
    fontFamily: 'Poppins, sans-serif',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: active ? '#132F5F' : '#eaeaea',
    },
}));

interface ApprovalFilterProps {
    selectedTab: number;
    searchQuery: string;
    searchField: string;
    onTabChange: (tab: number) => void;
    onSearchChange: (query: string) => void;
    onSearchFieldChange: (field: string) => void;
    onSearch: () => void;
}

const ApprovalFilter: React.FC<ApprovalFilterProps> = ({
    selectedTab,
    searchQuery,
    searchField,
    onTabChange,
    onSearchChange,
    onSearchFieldChange,
    onSearch,
}) => {
    const [exportOpen, setExportOpen] = React.useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isLargeTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const getSearchPlaceholder = (field: string) => {
        switch (field) {
            case 'Electricity Meter No':
                return 'Search Electricity Meter No';
            case 'Personal ID':
                return 'Search Personal ID';
            case 'Account Number':
                return 'Search Account Number';
            case 'Indigent Number':
                return 'Search Indigent Number';
            default:
                return 'Search Electricity Meter No';
        }
    };

    return (
        <Box sx={{ 
            position: 'relative', 
            width: '100%', 
            overflow: 'hidden', 
            maxWidth: '100%',
            boxSizing: 'border-box',
            px: { xs: 1, sm: 1.5, md: 2, lg: 3 },
        }}>
            {/* Tabs Section - Using flexbox with proper alignment */}
            <Box
                sx={{
                    mb: { xs: 2, sm: 2.5, md: 3 },
                    pb: 1,
                    width: '100%',
                    maxWidth: '100%',
                }}
            >
                <Box 
                    sx={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: { xs: 1, sm: 1.5, md: 2 },
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    {tabs.map((label, idx) => (
                        <PillButton
                            key={label}
                            active={selectedTab === idx}
                            onClick={() => onTabChange(idx)}
                            disableElevation
                            disableRipple
                            sx={{
                                fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '14px' },
                                px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
                                minWidth: { xs: 'auto', sm: 'auto', md: 'auto' },
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                height: { xs: '36px', sm: '38px', md: '40px', lg: '44px' },
                            }}
                        >
                            {label}
                        </PillButton>
                    ))}
                </Box>
            </Box>

            {/* Search and Export Section */}
            <Stack
                direction={isMobile ? 'column' : 'row'}
                justifyContent='space-between'
                alignItems={isMobile ? 'stretch' : 'center'}
                mb={3}
                spacing={isMobile ? 2 : 0}
                gap={isMobile ? 2 : (isTablet ? 2 : 3)}
                sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
            >
                {/* Search Box - Restored original design with proper block padding */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '32px',
                        backgroundColor: '#EEF2FF',
                        px: { xs: 2, sm: 2.5, md: 3 },
                        py: { xs: 1.5, sm: 1.75, md: 2 },
                        width: isMobile ? '100%' : (isTablet ? '70%' : (isLargeTablet ? '75%' : '80%')),
                        flexGrow: 1,
                        maxWidth: { xs: '100%', sm: '100%' },
                        minWidth: 0,
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}
                >
                    <Select
                        size='small'
                        value={searchField}
                        onChange={e => onSearchFieldChange(e.target.value)}
                        sx={{
                            mr: { xs: 2, sm: 2.5, md: 3 },
                            width: { xs: '120px', sm: '140px', md: '160px', lg: '175px' },
                            height: { xs: '44px', sm: '46px', md: '48px' },
                            borderRadius: '52px',
                            border: 'none',
                            background: '#fff',
                            fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '14px' },
                            padding: '12px',
                            fontFamily: 'Poppins, sans-serif',
                            flexShrink: 0,
                            '& .MuiSelect-select': {
                                padding: 0,
                                fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '14px' },
                            },
                            '& fieldset': { border: 'none' },
                        }}
                        variant='standard'
                        disableUnderline
                        displayEmpty
                    >
                        <MenuItem value='Electricity Meter No' sx={{ fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '14px' } }}>
                            {isMobile ? 'Meter No' : 'Electricity Meter No'}
                        </MenuItem>
                        <MenuItem value='Personal ID' sx={{ fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '14px' } }}>
                            Personal ID
                        </MenuItem>
                        <MenuItem value='Account Number' sx={{ fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '14px' } }}>
                            {isMobile ? 'Account No' : 'Account Number'}
                        </MenuItem>
                        <MenuItem value='Indigent Number' sx={{ fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '14px' } }}>
                            {isMobile ? 'Indigent No' : 'Indigent Number'}
                        </MenuItem>
                    </Select>

                    <InputBase
                        placeholder={getSearchPlaceholder(searchField)}
                        value={searchQuery}
                        onChange={e => onSearchChange(e.target.value)}
                        onKeyPress={e => {
                            if (e.key === 'Enter') {
                                onSearch();
                            }
                        }}
                        sx={{
                            flex: 1,
                            fontSize: { xs: '13px', sm: '14px', md: '15px', lg: '16px' },
                            fontFamily: 'Poppins, sans-serif',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    />

                    <IconButton
                        sx={{
                            backgroundColor: '#132F5F',
                            color: '#fff',
                            ml: { xs: 1, sm: 1.5, md: 2 },
                            width: { xs: 48, sm: 50, md: 52 },
                            height: { xs: 48, sm: 50, md: 52 },
                            flexShrink: 0,
                            '&:hover': {
                                backgroundColor: '#0f254d',
                            },
                        }}
                        onClick={onSearch}
                    >
                        <SearchIcon sx={{ fontSize: { xs: '20px', sm: '22px', md: '24px' } }} />
                    </IconButton>
                </Box>

                {/* Export Button - Now smaller and better positioned */}
                <Box 
                    textAlign='center' 
                    sx={{ 
                        position: isMobile ? 'static' : 'relative', 
                        width: isMobile ? '100%' : 'auto',
                        flexShrink: 0,
                        maxWidth: { xs: '100%', sm: 'auto' },
                        boxSizing: 'border-box',
                    }}
                >
                    <Button
                        variant='contained'
                        startIcon={<FileUploadOutlinedIcon sx={{ fontSize: { xs: '16px', sm: '18px', md: '20px' } }} />}
                        onClick={() => setExportOpen(true)}
                        sx={{
                            backgroundColor: '#132F5F',
                            borderRadius: '12px',
                            textTransform: 'none',
                            width: { xs: '100%', sm: '100px', md: '110px', lg: '120px' },
                            fontSize: { xs: '12px', sm: '13px', md: '14px', lg: '15px' },
                            lineHeight: '24px',
                            fontWeight: 500,
                            px: { xs: 2, sm: 2.5, md: 3 },
                            py: { xs: 1, sm: 1.25, md: 1.5 },
                            height: { xs: 48, sm: 50, md: 52 },
                            fontFamily: 'Poppins, sans-serif',
                            '&:hover': {
                                backgroundColor: '#0f254d',
                            },
                        }}
                    >
                        Export
                    </Button>
                    <Typography
                        sx={{
                            fontSize: { xs: '10px', sm: '11px', md: '12px', lg: '13px' },
                            mt: 0.5,
                            color: '#666',
                            fontFamily: 'Poppins, sans-serif',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        Export data in pdf or csv
                    </Typography>
                </Box>
            </Stack>

            <ExportReportModal open={exportOpen} onClose={() => setExportOpen(false)} />
        </Box>
    );
};

export default ApprovalFilter;
